import { Link } from 'react-router-dom';
import type { Project } from '@/api';
import { typeMeta } from '../projectTypes';
import { relativeTime } from '../format';

export default function ProjectsView({
  projects,
  scores,
  onNewPlaybook,
  onDelete,
  creating,
  createErr,
}: {
  projects: Project[];
  scores: Record<string, number>;
  onNewPlaybook: () => void;
  onDelete: (id: string) => void;
  creating?: boolean;
  createErr?: string | null;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-textp">Playbooks</h1>
          <p className="text-sm text-texts mt-0.5">
            {projects.length} {projects.length === 1 ? 'playbook' : 'playbooks'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/marketplace"
            className="whitespace-nowrap text-sm text-texts hover:text-textp"
          >
            Marketplace
          </Link>
          <button
            data-testid="new-playbook"
            onClick={onNewPlaybook}
            disabled={creating}
            className="whitespace-nowrap flex items-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-60 px-4 py-2 rounded-lg transition-colors"
          >
            <i className="ri-add-line" />
            {creating ? 'Creating…' : 'New Playbook'}
          </button>
        </div>
      </div>

      {createErr && (
        <div className="mt-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{createErr}</div>
      )}

      {projects.length === 0 ? (
        <div data-testid="playbook-chooser" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            data-testid="chooser-new-playbook"
            onClick={onNewPlaybook}
            disabled={creating}
            className="text-left bg-surface border border-border hover:border-accentLine rounded-xl p-8 disabled:opacity-60"
          >
            <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accentSoft border border-accentLine">
              <i className="ri-quill-pen-line text-accent text-xl" />
            </span>
            <h2 className="mt-4 text-base font-semibold text-textp">New Playbook</h2>
            <p className="mt-1 text-sm text-texts">Build a playbook from scratch. Chat about the outcome, then run it.</p>
          </button>
          <Link
            to="/marketplace"
            data-testid="chooser-run-playbook"
            className="text-left bg-surface border border-border hover:border-accentLine rounded-xl p-8"
          >
            <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accentSoft border border-accentLine">
              <i className="ri-play-circle-line text-accent text-xl" />
            </span>
            <h2 className="mt-4 text-base font-semibold text-textp">Run a playbook</h2>
            <p className="mt-1 text-sm text-texts">Use a published playbook from the marketplace. Fork it into Studio, then press Run.</p>
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {projects.map((p) => {
            const type = typeMeta(p.outcome_type);
            const score = p.outcome_id ? scores[p.outcome_id] : undefined;
            return (
              <div
                key={p.id}
                className="group bg-surface border border-border rounded-xl p-5 hover:border-accentLine transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-accentSoft border border-accentLine flex items-center justify-center">
                      <i className={`${type.icon} text-accent text-lg`} />
                    </span>
                    <Link
                      to={`/studio?project=${p.id}${p.outcome_id ? `&outcome=${p.outcome_id}` : ''}`}
                      className="text-base font-semibold text-textp hover:text-accent transition-colors truncate"
                    >
                      {p.title}
                    </Link>
                  </div>
                  <span className="shrink-0 text-sm font-mono text-success">
                    {score == null ? '—' : `${score}/12`}
                  </span>
                </div>

                <p className="mt-1 text-sm text-texts line-clamp-2">{p.outcome_description}</p>

                <div className="mt-3 flex items-center justify-between">
                  <span className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs text-texts bg-surface2 border border-border px-2.5 py-1 rounded-full">
                    <i className={type.icon} />
                    {type.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-texts">{relativeTime(p.updated_at)}</span>
                    <button
                      onClick={() => onDelete(p.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-texts hover:text-error hover:bg-surface2 transition-colors"
                      title="Delete playbook"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
